import "react-loading-skeleton/dist/skeleton.css";
import "../scss/style.scss";

import "./app/i18n"; // <<< --- IMPORT i18n CONFIGURATION EARLY ---

// Import store to ensure it's initialized
import { store } from "./app/store";

// Import hydration system
import "./app/hydrate";

// Import Redux actions for Mega Menu
import {
  openCategoryMegaMenu,
  closeCategoryMegaMenu,
} from "./features/header/headerUiSlice";

import "./features/home/home";
import "./features/about/about";

// Import footer accordion logic
import "./features/footer/footer.js";

/**
 * This is the main entry point for the JS application.
 * It's responsible for initializing the app and attaching event listeners.
 */
const initApp = (): void => {
  console.log("E-commerce application initialized");
  setupMegaMenuHover(); // Initialize mega menu interactions
};

// --- Mega Menu Hover Logic ---
let closeMenuTimeout: NodeJS.Timeout | null = null;
let isMouseOverMenu = false; // Track if mouse is over the menu itself
let isMouseOverTrigger = false; // Track if mouse is over the trigger

function setupMegaMenuHover(): void {
  // Target the link using the data attribute added in PHP
  const shopTrigger = document.querySelector('a[data-megamenu-trigger="shop"]');
  // Target the React root element where the mega menu is rendered
  const megaMenuRoot = document.getElementById("category-mega-menu-root");

  if (!shopTrigger || !megaMenuRoot) {
    console.warn(
      "[MegaMenu] Trigger or Root element not found. Hover interaction disabled."
    );
    return;
  }

  const dispatchOpen = () => {
    if (closeMenuTimeout) clearTimeout(closeMenuTimeout); // Clear any pending close
    // Only open if not already open (optional optimization)
    if (!store.getState().headerUi.isCategoryMegaMenuOpen) {
      store.dispatch(openCategoryMegaMenu());
    }
  };

  const dispatchCloseWithDelay = () => {
    if (closeMenuTimeout) clearTimeout(closeMenuTimeout); // Clear existing timeout
    closeMenuTimeout = setTimeout(() => {
      // Double-check: Only close if the mouse is not over the trigger OR the menu
      if (!isMouseOverTrigger && !isMouseOverMenu) {
        store.dispatch(closeCategoryMegaMenu());
      }
    }, 250); // Adjust delay (milliseconds)
  };

  // --- Event Listeners ---

  // Trigger (Shop Link) Listeners
  shopTrigger.addEventListener("mouseenter", () => {
    isMouseOverTrigger = true;
    dispatchOpen();
  });

  shopTrigger.addEventListener("mouseleave", () => {
    isMouseOverTrigger = false;
    dispatchCloseWithDelay();
  });

  // Add focus/blur for keyboard navigation (basic)
  shopTrigger.addEventListener("focus", () => {
    isMouseOverTrigger = true; // Treat focus like mouseenter for logic
    dispatchOpen();
  });
  shopTrigger.addEventListener("blur", () => {
    // When focus leaves the trigger, start the close delay
    // Requires relatedTarget checks if tabbing *into* the menu
    isMouseOverTrigger = false;
    // A more complex focus management might be needed for robust keyboard nav
    // dispatchCloseWithDelay(); // Simple version
  });

  // Menu Root Element Listeners (Catches events bubbling from React component)
  megaMenuRoot.addEventListener("mouseenter", () => {
    isMouseOverMenu = true;
    if (closeMenuTimeout) clearTimeout(closeMenuTimeout); // Clear close timer if mouse enters menu
  });

  megaMenuRoot.addEventListener("mouseleave", () => {
    isMouseOverMenu = false;
    dispatchCloseWithDelay(); // Start close timer when mouse leaves menu
  });

  // Focus within the menu should keep it open
  megaMenuRoot.addEventListener("focusin", () => {
    isMouseOverMenu = true; // Treat focusin like mouseenter
    if (closeMenuTimeout) clearTimeout(closeMenuTimeout);
  });

  // Focus leaving the menu (needs refinement for robust keyboard nav)
  megaMenuRoot.addEventListener("focusout", (e) => {
    // Check if the new focused element is still within the menu root
    if (!megaMenuRoot.contains(e.relatedTarget as Node)) {
      isMouseOverMenu = false;
      dispatchCloseWithDelay();
    }
  });

  console.log("[MegaMenu] Hover listeners attached.");
}

// --- Redux Subscription for Class Toggling ---
// This ensures the CSS class is applied/removed based on the actual Redux state
store.subscribe(() => {
  const isMegaMenuOpen = store.getState().headerUi.isCategoryMegaMenuOpen;
  // Find the *rendered* React component's main div inside the root
  const megaMenuElement = document.querySelector(
    "#category-mega-menu-root .category-mega-menu"
  );

  if (megaMenuElement) {
    if (isMegaMenuOpen) {
      megaMenuElement.classList.add("is-open");
    } else {
      megaMenuElement.classList.remove("is-open");
    }
  }
});
// --- End Redux Subscription ---

// Initialize the app when DOM is ready
document.addEventListener("DOMContentLoaded", initApp);
